import React from 'react';
import type { AttentionItem } from '../../domain/types';
import { BrandIcon, SlackRichText } from '../../ui/common';
import type { ComposerMentionCandidate } from '../../ui/common/RichTextComposer';
import { ProviderItemDetail } from '../detail/ProviderItemDetail';
import { SlackCardExpanded } from './SlackCardExpanded';

export interface SlackDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: AttentionItem;
  summaryOpen: boolean;
  isInMerge: boolean;
  replyDraft: string;
  isSending: boolean;
  isDrafting: boolean;
  sendError: string | null | undefined;
  expandedRootRef: React.Ref<HTMLDivElement>;
  threadEndRef: React.Ref<HTMLDivElement>;
  onScrollToEnd: () => void;
  onToggleMerge: (e: React.MouseEvent) => void;
  onToggleSummary: (e: React.MouseEvent) => void;
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
  onReplyChange: (value: string) => void;
  onSubmitReply: () => void;
  onAskAboutThread: (e: React.MouseEvent) => void;
  onGenerateDraft: (e: React.MouseEvent) => void;
  mentionCandidates?: ComposerMentionCandidate[];
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  onSend: (e: React.MouseEvent) => void;
}

/**
 * Complete Slack thread detail, built on the shared `ProviderItemDetail`
 * shell (tasks.md §4). Reuses `SlackCardExpanded` (full hydrated transcript,
 * participants, thread summary, and reply composer) as the body rather than
 * duplicating it — the shell only supplies the sticky header/dismissal
 * chrome around that existing content.
 */
export const SlackDetailModal: React.FC<SlackDetailModalProps> = ({
  isOpen,
  onClose,
  item,
  ...expandedProps
}) => {
  if (!isOpen) return null;

  return (
    <ProviderItemDetail
      isOpen={isOpen}
      onClose={onClose}
      testId="slack-detail-modal"
      ariaLabel={`Detalle de hilo ${item.primary || item.title}`}
      header={
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--z-fill-2)',
            background: 'var(--z-surface-subtle)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <BrandIcon source="slack" size={18} />
            <span
              data-testid="slack-modal-identity"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--z-text)',
              }}
            >
              {item.primary}
            </span>
            <h1
              data-testid="slack-modal-title"
              style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--z-text)' }}
            >
              <SlackRichText text={item.title} />
            </h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {item.rawSlackThread?.permalink ? (
              <a
                href={item.rawSlackThread.permalink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  height: 28,
                  padding: '0 10px',
                  borderRadius: 9,
                  border: '1px solid var(--z-border)',
                  background: 'var(--z-surface)',
                  color: 'var(--z-muted-2)',
                  fontSize: 11.5,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                <span>Abrir en Slack</span>
                <span style={{ fontSize: 10 }}>↗</span>
              </a>
            ) : null}
            <button
              type="button"
              data-testid="slack-modal-close"
              aria-label="Cerrar modal"
              onClick={onClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'var(--z-fill-2)',
                color: 'var(--z-text-2)',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>
        </header>
      }
    >
      <div style={{ padding: '20px 24px' }}>
        <SlackCardExpanded item={item} {...expandedProps} />
      </div>
    </ProviderItemDetail>
  );
};
